import { NavLink, useNavigate } from 'react-router-dom';
import { LogOut, ChevronLeft } from 'lucide-react';
import { NAV_SECTIONS } from './nav-config';
import { useAuthStore } from '@/shared/store/auth';
import { initials } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { apiFetch } from '@/shared/api/client';

interface SidebarProps {
  /** En móvil/tablet el sidebar es un drawer; controla su visibilidad. */
  mobileOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ mobileOpen, onClose }: SidebarProps): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const onLogout = (): void => {
    // Best-effort: invalidamos la cookie de refresh en el backend, pero
    // ante cualquier fallo (red caída, sesión ya expirada) procedemos al
    // logout local de igual forma — la cookie está path-scoped a /api/auth
    // y se limpiará en el próximo login.
    void apiFetch<unknown>('/api/auth/logout', { method: 'POST' }).catch(() => {
      /* swallow */
    });
    logout();
    navigate('/login', { replace: true });
  };

  const userIni = user
    ? initials(user.nombre.split(' ')[0] ?? '', user.nombre.split(' ').slice(1).join(' ') || ' ')
    : 'EM';

  return (
    <>
      {/* Backdrop — solo móvil/tablet cuando el drawer está abierto */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          'w-[248px] flex-shrink-0 h-screen bg-surface border-r border-[#eef2f7] flex flex-col',
          // Desktop: columna estática pegajosa.
          'lg:sticky lg:top-0 lg:translate-x-0',
          // Móvil/tablet: drawer off-canvas por encima del contenido.
          'fixed inset-y-0 left-0 z-50 transition-transform duration-200 ease-out lg:z-auto',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
      {/* Brand */}
      <div className="px-[18px] py-5 flex items-center gap-3 border-b border-[#eef2f7] relative">
        <img
          src="/logo-endomedical.png"
          alt="Endomedical"
          className="h-9 w-auto max-w-[180px] object-contain"
        />
        {/* Cerrar (móvil) */}
        <button
          type="button"
          title="Cerrar menú"
          onClick={onClose}
          className="ml-auto lg:hidden p-1.5 rounded-md text-text-muted hover:text-primary"
        >
          <ChevronLeft className="h-5 w-5" strokeWidth={2.2} />
        </button>
        {/* Colapsar (desktop, placeholder existente) */}
        <button
          type="button"
          title="Colapsar menú"
          className="hidden lg:grid absolute -right-3 top-6 w-6 h-6 rounded-full bg-white border border-[#eef2f7] text-text-muted place-items-center shadow-sm hover:text-primary hover:border-primary"
        >
          <ChevronLeft className="h-3 w-3" strokeWidth={2.4} />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-3.5">
        {NAV_SECTIONS.map((section) => {
          const visibleItems = section.items.filter(
            (item) =>
              item.roles === undefined ||
              (user !== null && item.roles.includes(user.rol)),
          );
          // Si el rol no puede ver ningún item de la sección, la ocultamos por
          // completo (título incluido) — p. ej. "SISTEMA" para no-administradores.
          if (visibleItems.length === 0) return null;
          return (
          <div key={section.title} className="mb-3.5">
            <div className="text-[10.5px] font-bold tracking-[0.14em] uppercase text-text-muted px-3 pt-2.5 pb-1.5">
              {section.title}
            </div>
            {visibleItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-[10px] text-[13.5px] font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-white font-semibold shadow-[0_4px_12px_rgba(58,163,147,0.28)]'
                        : 'text-slate-700 hover:bg-slate-100 hover:text-text',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className={cn(
                          'w-[18px] h-[18px] flex-shrink-0',
                          isActive ? 'text-white' : 'text-text-muted',
                        )}
                      />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge !== undefined && (
                        <span
                          className={cn(
                            'min-w-[20px] h-5 px-1.5 rounded-full text-[10.5px] font-bold grid place-items-center',
                            isActive
                              ? 'bg-white/25 text-white'
                              : 'bg-red-500 text-white',
                          )}
                        >
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
          );
        })}
      </nav>

      {/* User block */}
      <div className="px-3.5 py-3.5 border-t border-[#eef2f7] flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-primary-soft text-primary grid place-items-center font-bold text-[13px]">
          {userIni}
        </div>
        <div className="flex flex-col leading-tight min-w-0 flex-1">
          <strong className="text-[13px] text-text truncate">
            {user?.nombre ?? 'Sin sesión'}
          </strong>
          <span className="text-[11px] text-text-muted uppercase tracking-wider mt-0.5">
            {user?.rolLabel ?? '—'}
          </span>
        </div>
        <button
          type="button"
          onClick={onLogout}
          title="Cerrar sesión"
          className="ml-auto p-1.5 rounded-md text-text-muted hover:text-danger hover:bg-danger-soft transition-colors"
        >
          <LogOut className="w-[18px] h-[18px]" />
        </button>
      </div>
      </aside>
    </>
  );
}
